
import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

type Profile = {
  id: string;
  first_name: string | null;
  last_name: string | null;
};

type AuthContextType = {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  associateGameWithUser: (gameId: string) => Promise<boolean>;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  signOut: async () => {},
  signInWithGoogle: async () => {},
  associateGameWithUser: async () => false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const processUserMetadata = (user: User) => {
    const metadata = user.user_metadata || {};
    let firstName = metadata.first_name;
    let lastName = metadata.last_name;
    
    // If Google authentication provides a full_name or name but no first_name/last_name
    if (!firstName && (metadata.full_name || metadata.name)) {
      const fullName = metadata.full_name || metadata.name;
      const nameParts = fullName.split(' ');
      firstName = nameParts[0];
      // Join the rest as last name if there are multiple parts
      lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      // Update the user metadata in Supabase
      updateUserMetadata(user.id, { first_name: firstName, last_name: lastName })
        .catch(error => console.error("Error updating user metadata:", error));
    }
    
    return {
      id: user.id,
      first_name: firstName || null,
      last_name: lastName || null
    };
  };

  const updateUserMetadata = async (userId: string, metadata: Record<string, any>) => {
    const { error } = await supabase.auth.updateUser({
      data: metadata
    });
    
    if (error) {
      throw error;
    }
  };

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user || null);
        
        if (session?.user) {
          setProfile(processUserMetadata(session.user));
        }
      } catch (error) {
        console.error("Error getting initial session:", error);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user || null);
        
        if (session?.user) {
          setProfile(processUserMetadata(session.user));
        } else {
          setProfile(null);
        }
        
        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const signInWithGoogle = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/login`,
      },
    });
  };

  const associateGameWithUser = async (gameId: string): Promise<boolean> => {
    if (!user) return false;
    
    try {
      // Update the game record to associate it with the current user
      const { error } = await supabase
        .from("games")
        .update({ user_id: user.id })
        .eq("id", gameId);
      
      if (error) {
        console.error("Error associating game with user:", error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error("Error in associateGameWithUser:", error);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, signOut, signInWithGoogle, associateGameWithUser }}>
      {children}
    </AuthContext.Provider>
  );
};
